import { Schema } from "effect";

export const PapersFlowBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const CanonicalIdentifierSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(180),
  Schema.makeFilter(
    (value) =>
      value === value.trim() &&
      Array.from(value).every((character) => {
        const codePoint = character.codePointAt(0);
        return codePoint !== undefined && codePoint > 31 && codePoint !== 127;
      }),
    { expected: "a canonical identifier without surrounding whitespace or control characters" },
  ),
);

export const IsoDateTimeSchema = Schema.String.check(
  Schema.isMaxLength(40),
  Schema.makeFilter(
    (value) =>
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/u.test(value) &&
      Number.isFinite(Date.parse(value)),
    { expected: "an ISO-8601 UTC timestamp" },
  ),
);

export const IanaTimeZoneSchema = Schema.Trim.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(80),
  Schema.makeFilter((value) => {
    if (
      value !== "UTC" &&
      !/^[A-Za-z][A-Za-z0-9_+-]*\/[A-Za-z0-9_+-]+(?:\/[A-Za-z0-9_+-]+)?$/u.test(value)
    ) {
      return "must be UTC or a canonical IANA time-zone name";
    }
    try {
      const resolvedTimeZone = new Intl.DateTimeFormat("en-US", {
        timeZone: value,
      }).resolvedOptions().timeZone;
      return resolvedTimeZone.length > 0 || "must be a supported IANA time-zone name";
    } catch {
      return "must be a supported IANA time-zone name";
    }
  }),
);

export const HttpUrlSchema = Schema.String.check(
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => /^https?:\/\/[^\s/?#@]+(?:[/?#]|$)/iu.test(value), {
    expected: "an HTTP URL without embedded credentials",
  }),
);

export const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

export const toPapersFlowValidationSchema = <S extends Schema.ConstraintDecoder<unknown>>(
  schema: S,
) =>
  Schema.toStandardSchemaV1(schema, {
    parseOptions: PapersFlowBoundaryParseOptions,
  });

export const toPapersFlowStandardSchema = <S extends Schema.ConstraintDecoder<unknown>>(
  schema: S,
) => Schema.toStandardJSONSchemaV1(toPapersFlowValidationSchema(schema));
